// src/pages/VatTypesPage.jsx
import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import axios from '../../axios';
import API_BASE_URL from '../../config';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Typography, Paper, Box, Divider } from '@mui/material';

export default function VatTypesPage() {
  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  const [vatTypes, setVatTypes] = useState([]);
  const [form, setForm] = useState({ companyId, name: '', rate: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (companyId) fetchVatTypes();
  }, [companyId]);

  const fetchVatTypes = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/VatTypes?companyId=${companyId}`);
      setVatTypes(res.data);
    } catch (err) {
      console.error('❌ خطأ تحميل أنواع الضريبة:', err);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: name === 'rate' ? parseFloat(value) : value }));
  };

  const handleAdd = async () => {
    if (!form.name || form.rate < 0) {
      Swal.fire('تنبيه', 'الرجاء إدخال اسم ونسبة ضريبة صحيحة', 'warning');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/VatTypes`, { ...form, rate: form.rate / 100 });
      Swal.fire({ icon: 'success', title: 'تم الإضافة بنجاح', timer: 1500, showConfirmButton: false });
      setForm({ companyId, name: '', rate: 0 });
      fetchVatTypes();
    } catch (err) {
      Swal.fire('خطأ', err.response?.data?.message || 'حدث خطأ أثناء الإضافة', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    const result = await Swal.fire({
      title: 'هل أنت متأكد؟',
      text: "لن تتمكن من التراجع عن هذا الحذف!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'نعم، احذف!'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_BASE_URL}/VatTypes/${id}`);
        fetchVatTypes();
      } catch (err) {
        Swal.fire('خطأ', 'حدث خطأ أثناء الحذف', 'error');
      }
    }
  };

  return (
    <Box sx={{ maxWidth: '800px', mx: 'auto', mt: 6, px: 2 }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px -6px rgba(15, 23, 42, 0.04)' }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 3, color: '#1e293b' }}>
          📑 إدارة أنواع الضريبة
        </Typography>

        {/* نموذج الإضافة */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' }, gap: 2, mb: 4 }}>
          <Input name="name" placeholder="اسم الضريبة (مثلاً: ضريبة 15%)" value={form.name} onChange={handleChange} />
          <Input name="rate" type="number" step="0.01" placeholder="النسبة (%)" value={form.rate} onChange={handleChange} />
          <Button onClick={handleAdd} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 font-bold rounded-xl">
            {loading ? 'جاري الإضافة...' : '➕ إضافة'}
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* الجدول */}
        <Box sx={{ overflowX: 'auto' }}>
          <table className="w-full text-sm">
            <thead className="text-slate-500 uppercase text-xs">
              <tr className="text-center">
                <th className="py-3 px-4 border-b">#</th>
                <th className="py-3 px-4 border-b text-right">اسم الضريبة</th>
                <th className="py-3 px-4 border-b">النسبة (%)</th>
                <th className="py-3 px-4 border-b">إجراء</th>
              </tr>
            </thead>
            <tbody>
              {vatTypes.map((v, idx) => (
                <tr key={v.id} className="hover:bg-slate-50 transition-colors text-center border-b border-slate-50">
                  <td className="py-4 font-bold text-slate-400">{idx + 1}</td>
                  <td className="py-4 text-right font-semibold text-slate-700">{v.name}</td>
                  <td className="py-4 font-bold">{(v.rate * 100).toFixed(2)}%</td>
                  <td className="py-4">
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(v.id)} className="rounded-lg">🗑️</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Paper>
    </Box>
  );
}